import { builder, Action, PlayerId, Player, Card, GameState, Question, isColorQuestion, Colors, CardCategory, ActionMode } from '.';
import Vue from 'vue';
import _ from 'lodash';
import { getInformantCardLetter, questionAsSentence, getCardName, getCardFilename } from '@/functions';

type PlayerJoinedResponse = {
    gid: string,
    player: {
        name: string,
        id: string
    },
    players: Player[],
    playerInfo?: {
        id: string,
        name: string,
        isOwner: boolean
    }
}

type NextTurnResponse = {
    action: Action.nextTurn,
    turn: number,
    currentPlayerId: string,
    tableGlasses: number,
    playersGlasses: {
        [playerId: string]: number
    },
    gotGlassFromTable: boolean,
    needGlass: boolean,
    timestamp: string
}

type GameStartedResponse = {
    sortedPlayers: PlayerId[],
    tableCards: number,
    tableGlasses: number,
    playersGlasses: {
        [playerId: string]: number
    },
    secretCards: Card[],
    playersPublicCards: { [pid: string]: [Card, Card, Card] },
    allDeckCards: Card[],
    timestamp: string
}

type WitnessInfoResponse = {
    witnessId: PlayerId,
    playersGlasses: {
        [playerId: string]: number
    },
    question: Question,
    matchingCardsCount: number,
    timestamp: string
}

type InformantCardResponse = {
    cardIndex: number,
    playerWithoutGlasses: PlayerId | undefined, //per notificare che ha ricevuto una lente
    playersGlasses: {
        [playerId: string]: number
    },
    informantCard?: Card,
    timestamp: string
}

type AccuseResponse = {
    correct: boolean,
    cards: Card[],
    gameEnded: boolean,
    winner: PlayerId | undefined,
    playerWithoutGlasses: PlayerId | undefined,
    playersGlasses: {
        [playerId: string]: number
    },
    playersPublicCards: { [pid: string]: [Card, Card, Card] } | undefined,
    tableGlasses: number,
    timestamp: string
}

type GetPlayerGlassResponse = {
    playersGlasses: {
        [playerId: string]: number
    },
    tableGlasses: number,
    glassPlayerId: PlayerId,
    timestamp: string
}

export const commitPlayerJoined = builder.commit((state, data: PlayerJoinedResponse) => {
    state.players = data.players;
    if (data.playerInfo) {
        state.gid = data.gid;
        state.playerInfo = data.playerInfo;
        state.isOwner = data.playerInfo.isOwner;
    }
}, "commitPlayerJoined");

export const commitPlayerLeft = builder.commit((state, { playerId }: { playerId: string }) => {
    state.playerLeft = playerId;
    state.gameEnded = true;
}, "commitPlayerLeft");

export const commitGameStarted = builder.commit((state, data: GameStartedResponse) => {
    state.sortedPlayers = _.map(data.sortedPlayers, pid => _.find(state.players, p => p.id == pid)!);
    state.tableCards = data.tableCards;
    state.tableGlasses = data.tableGlasses;
    state.playersGlasses = data.playersGlasses;
    state.secretCards = data.secretCards;
    state.playersPublicCards = data.playersPublicCards;
    state.allDeckCards = _.map(data.allDeckCards, c => {
        return {
            ...c,
            src: getCardFilename(c)
        }
    });
    state.gameStarted = true;
}, "commitGameStarted");

export const commitNextTurn = builder.commit((state, data: NextTurnResponse) => {
    state.turn = data.turn;
    state.currentPlayerId = data.currentPlayerId;
    state.ui.gotGlassFromTable = data.gotGlassFromTable;
    state.ui.needGlass = data.needGlass;
    state.tableGlasses = data.tableGlasses;
    state.playersGlasses = data.playersGlasses;

    state.ui.showNeedGlassDlg = state.ui.needGlass;

    //reset state
    state.witnessInfo = undefined;
    state.informantCard = undefined;
    state.accuse = undefined;

    const currentPlayer = getPlayerName(state, state.currentPlayerId);
    if (state.turn == 0) {
        log(state, data.timestamp, `${currentPlayer} dà inizio alle indagini`);
    } else {
        log(state, data.timestamp, `Le indagini passano a ${currentPlayer}`);
    }
    if (data.gotGlassFromTable) {
        log(state, data.timestamp, `${currentPlayer} riceve una lente dal tavolo`);
    }
}, "commitNextTurn");

export const commitWitnessInfo = builder.commit((state, data: WitnessInfoResponse) => {
    state.playersGlasses = data.playersGlasses;
    state.witnessInfo = {
        matchingCardsCount: data.matchingCardsCount,
        question: data.question,
        witnessId: data.witnessId
    }

    const currentPlayer = getPlayerName(state, state.currentPlayerId),
        witnessName = getPlayerName(state, data.witnessId),
        question = questionAsSentence(data.question);

    log(state, data.timestamp, `${currentPlayer} dà una lente a ${witnessName}`);
    log(state, data.timestamp, `${currentPlayer} chiede a ${witnessName} ${question}. ${witnessName} risponde: ${data.matchingCardsCount}`);
}, "commitWitnessInfo");

export const commitInformantCard = builder.commit((state, data: InformantCardResponse) => {
    state.playersGlasses = data.playersGlasses;
    state.informantCard = {
        cardIndex: data.cardIndex,
        playerWithoutGlasses: data.playerWithoutGlasses,
        informantCard: data.informantCard
    };

    const currentPlayer = getPlayerName(state, state.currentPlayerId),
        cardLetter = getInformantCardLetter(data.cardIndex);

    logGlassBeneficiary(state, data.timestamp, currentPlayer, data.playerWithoutGlasses);
    log(state, data.timestamp, `${currentPlayer} guarda la carta ${cardLetter} dell'informatore segreto`);
}, "commitInformantCard");

export const commitAccuse = builder.commit((state, data: AccuseResponse) => {
    state.playersGlasses = data.playersGlasses;
    state.gameEnded = data.gameEnded;
    state.winner = data.winner;
    state.playersGlasses = data.playersGlasses;
    state.tableGlasses = data.tableGlasses;
    state.accuse = {
        cards: data.cards,
        correct: data.correct,
        playerWithoutGlasses: data.playerWithoutGlasses
    };
    if (data.gameEnded) {
        state.playersPublicCards = data.playersPublicCards!;
    }
    const currentPlayer = getPlayerName(state, state.currentPlayerId);

    logGlassBeneficiary(state, data.timestamp, currentPlayer, data.playerWithoutGlasses);
    log(state, data.timestamp, `${currentPlayer} formula l'accusa: ${getCardName(data.cards[0])}, ${getCardName(data.cards[1])}, ${getCardName(data.cards[2])}`);
    if (data.correct) {
        state.gameLog += "\n\n";
        state.gameLog += `Il caso è risolto! Vince ${currentPlayer}`
    } else {
        log(state, data.timestamp, `L'accusa è infondata! ${currentPlayer} consegna le sue lenti al tavolo`)
    }

}, "commitAccuse");

export const commitGetPlayerGlass = builder.commit((state, data: GetPlayerGlassResponse) => {
    state.playersGlasses = data.playersGlasses;
    state.tableGlasses = data.tableGlasses;
    state.ui.needGlass = false;

    const currentPlayer = getPlayerName(state, state.currentPlayerId),
        glassPlayer = getPlayerName(state, data.glassPlayerId);

    log(state, data.timestamp, `${currentPlayer} prende una lente da ${glassPlayer}`);

}, "commitGetPlayerGlass");

export const commitToggleChooseQuestionDlg = builder.commit((state, { show, witnessId }: { show: boolean, witnessId?: PlayerId }) => {
    state.ui.showChooseQuestionDlg = show;
    state.ui.witnessId = witnessId;
}, "commitToggleChooseQuestionDlg");

export const commitToggleInformantDlg = builder.commit((state, { show, cardIndex }: { show: boolean, cardIndex?: number }) => {
    state.ui.showInformantDlg = show;
    state.ui.informantCardIndex = cardIndex;
}, "commitToggleInformantDlg");

export const commitToggleAccuseDlg = builder.commit((state, show: boolean) => {
    state.ui.showAccuseDlg = show;
}, "commitToggleAccuseDlg");

export const commitToggleNeedGlassDlg = builder.commit((state, show: boolean) => {
    state.ui.showNeedGlassDlg = show;
}, "commitToggleNeedGlassDlg");

export const commitToggleConfirmTakeGlassDlg = builder.commit((state, { show, playerId }: { show: boolean, playerId?: PlayerId }) => {
    state.ui.showConfirmTakeGlassDlg = show;
    state.ui.glassPlayerId = playerId || "";
}, "commitToggleConfirmTakeGlassDlg");

export const commitClearResponses = builder.commit((state) => {
    state.informantCard = undefined;
    state.witnessInfo = undefined;
    state.accuse = undefined;
}, "commitClearResponses");

function log(state: GameState, timestamp: string, msg: string) {
    const datetime = new Date(timestamp),
        time = datetime.toLocaleTimeString("it-IT", { hour: "numeric", minute: "numeric" });
    state.gameLog += (state.gameLog == "" ? "" : "\n") + `[${time}] ${msg}`;
}

function getPlayerName(state: GameState, pid: string) {
    return _
        .find(state.players, p => p.id == pid)!.name
}

function logGlassBeneficiary(state: GameState, timestamp: string, currentPlayer: string, beneficiaryId: PlayerId | undefined) {
    if (beneficiaryId) {
        log(state, timestamp, `${currentPlayer} dà una lente a ${getPlayerName(state, beneficiaryId)}`);
    } else {
        log(state, timestamp, `${currentPlayer} dà una lente al tavolo`);
    }
}


